export type GoogleMapsLibraries =
	| 'core'
	| 'maps'
	| 'places'
	| 'geocoding'
	| 'routes'
	| 'marker'
	| 'geometry'
	| 'elevation'
	| 'streetView'
	| 'journeySharing'
	| 'drawing'
	| 'visualization';

export interface GoogleMapsOptions {
	key: string;
	v?: 'weekly' | 'quarterly' | 'beta' | 'alpha';
	libraries?: GoogleMapsLibraries | Partial<GoogleMapsLibraries>[];
	language?: string;
	region?: string;
	authReferrerPolicy?: 'origin';
	mapIds?: string[];
}

export class GoogleMapsInitializer {
	private isSetup: boolean = false;
	private options: GoogleMapsOptions;

	constructor(options: GoogleMapsOptions) {
		this.options = options;
		if (this.isSetup || typeof document === 'undefined') {
			return;
		}

		this.options = options;
		this.initialize(options);
	}

	public getOptions() {
		return this.options;
	}

	private getStringOptions(rawOptions: GoogleMapsOptions): string {
		return JSON.stringify(rawOptions);
	}

	private async initialize(rawOptions: GoogleMapsOptions) {
		const options = this.getStringOptions(rawOptions);

		if (!options) {
			throw new Error('Google Maps Options can not be empty');
		}

		const googleMapScript = document.createElement('script');
		googleMapScript.setAttribute('type', 'text/javascript');

		googleMapScript.innerHTML = `
        ((g) => {
            var h,
            a,
            k,
            p = 'The Google Maps JavaScript API',
            c = 'google',
            l = 'importLibrary',
            q = '__ib__',
            m = document,
            b = window;
            b = b[c] || (b[c] = {});
            var d = b.maps || (b.maps = {}),
            r = new Set(),
            e = new URLSearchParams(),
            u = () =>
                h ||
                (h = new Promise(async (f, n) => {
                await (a = m.createElement('script'));
                e.set('libraries', [...r] + '');
                for (k in g)
                    e.set(
                    k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()),
                    g[k]
                    );
                e.set('callback', c + '.maps.' + q);
                a.src = \`https://maps.\${c}apis.com/maps/api/js?\` + e;
                d[q] = f;
                a.onerror = () => (h = n(Error(p + ' could not load.')));
                a.nonce = m.querySelector('script[nonce]')?.nonce || '';
                m.head.append(a);
                }));
            d[l]
            ? console.warn(p + ' only loads once. Ignoring:', g)
            : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
        })(${options});`;

		document.head.appendChild(googleMapScript);
		this.isSetup = true;
	}

	public isInitialized(interval: number = 100): Promise<boolean> {
		return new Promise<boolean>(resolve => {
			const validateInitialization = () => {
				if (this.isSetup) {
					resolve(true);
				} else {
					setTimeout(validateInitialization, interval);
				}
			};
			validateInitialization();
		});
	}
}
